import React, { useState } from 'react';
import { Box, Button, MenuItem, Select, Stack } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

const YEARS_BEFORE = 8;

const getRangeDayStyles = (theme, { isInRange, isRangeStart, isRangeEnd }) => {
  if (!isInRange && !isRangeStart && !isRangeEnd) {
    return {};
  }

  const isEdge = isRangeStart || isRangeEnd;
  let borderRadius = 0;
  if (isRangeStart && isRangeEnd) {
    borderRadius = '50%';
  } else if (isRangeStart) {
    borderRadius = '50% 0 0 50%';
  } else if (isRangeEnd) {
    borderRadius = '0 50% 50% 0';
  }

  return {
    borderRadius,
    backgroundColor: isEdge ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.12),
    color: isEdge ? theme.palette.primary.contrastText : theme.palette.text.primary,
    '&:hover, &:focus': {
      backgroundColor: isEdge ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.2),
    },
  };
};

const RangeDay = styled(PickersDay, {
  shouldForwardProp: (prop) => !['isInRange', 'isRangeStart', 'isRangeEnd'].includes(prop),
})(({ theme, isInRange, isRangeStart, isRangeEnd }) =>
  getRangeDayStyles(theme, { isInRange, isRangeStart, isRangeEnd }),
);

/**
 * Range selection built on the community DateCalendar: the first click starts a new
 * range, the next click on or after it closes the range.
 */
const PeriodRangeCalendar = ({ startDate, endDate, onChange, onClear, maxDate, labels = {} }) => {
  const effectiveMaxDate = maxDate ?? dayjs();
  const [visibleMonth, setVisibleMonth] = useState(() => startDate ?? dayjs());
  const [calendarKey, setCalendarKey] = useState(0);

  const months = Array.from({ length: 12 }, (_, index) => dayjs().month(index).format('MMMM'));
  const currentYear = dayjs().year();
  const years = Array.from({ length: YEARS_BEFORE + 1 }, (_, index) => currentYear - YEARS_BEFORE + index);

  const jumpTo = (nextMonth) => {
    setVisibleMonth(nextMonth);
    setCalendarKey((previous) => previous + 1);
  };

  const clampDay = (day) => {
    if (!day) {
      return day;
    }
    if (effectiveMaxDate && day.isAfter(effectiveMaxDate, 'day')) {
      return effectiveMaxDate.startOf('day');
    }
    return day;
  };

  const handleDaySelect = (day) => {
    if (!day) {
      return;
    }

    const selectedDay = clampDay(day);
    if (!selectedDay) {
      return;
    }

    const shouldStartNewRange = !startDate || (startDate && endDate) || selectedDay.isBefore(startDate, 'day');
    if (shouldStartNewRange) {
      onChange({ startDate: selectedDay.startOf('day'), endDate: null });
      return;
    }

    onChange({ startDate, endDate: selectedDay.endOf('day') });
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ px: 0.5, pb: 1 }}>
        <Select
          size="small"
          value={visibleMonth.month()}
          onChange={(event) => jumpTo(visibleMonth.month(Number(event.target.value)))}
          inputProps={{ 'aria-label': labels.month ?? 'Month' }}
          sx={{ flex: 1, height: 32 }}
        >
          {months.map((monthName, index) => (
            <MenuItem key={monthName} value={index}>
              {monthName}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={visibleMonth.year()}
          onChange={(event) => jumpTo(visibleMonth.year(Number(event.target.value)))}
          inputProps={{ 'aria-label': labels.year ?? 'Year' }}
          sx={{ width: 96, height: 32 }}
        >
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <DateCalendar
        key={calendarKey}
        value={null}
        referenceDate={visibleMonth}
        onChange={handleDaySelect}
        onMonthChange={setVisibleMonth}
        views={['day']}
        maxDate={effectiveMaxDate}
        slots={{ day: RangeDay }}
        slotProps={{
          day: (ownerState) => {
            const day = ownerState.day;
            return {
              isRangeStart: Boolean(startDate && day.isSame(startDate, 'day')),
              isRangeEnd: Boolean(endDate && day.isSame(endDate, 'day')),
              isInRange: Boolean(startDate && endDate && day.isAfter(startDate, 'day') && day.isBefore(endDate, 'day')),
            };
          },
        }}
        sx={{ width: 296, m: 0, '& .MuiDayCalendar-weekContainer': { margin: 0 } }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
        <Button size="small" onClick={onClear} sx={{ fontSize: 12 }}>
          {labels.clear ?? 'CLEAR'}
        </Button>
      </Box>
    </Box>
  );
};

export default PeriodRangeCalendar;
